import { useEffect, useState } from "react";
import { Contrast, Moon, Sun } from "lucide-react";
import { useA11y } from "@/contexts/AccessibilityContext";
import { FONT_SCALE_STEPS, isDark } from "@/lib/a11yPrefs";
// DISABLED: accent colour picker. When re-enabling, import PALETTES here too.

const tool =
  "inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-input bg-background px-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring aria-disabled:opacity-40 aria-pressed:bg-foreground aria-pressed:text-background";

/* DISABLED: accent colour picker. Uncomment this to bring it back (see also the matching blocks in index.html, a11yPrefs.ts, AccessibilityBar.tsx, accessibilityBar.test.tsx).
const swatch =
  "inline-flex h-8 w-8 items-center justify-center rounded-full border border-input bg-background transition-colors hover:bg-muted focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring aria-pressed:border-foreground aria-pressed:ring-2 aria-pressed:ring-foreground aria-pressed:ring-offset-1 aria-pressed:ring-offset-background";
*/

/**
 * Always-visible text size, contrast and theme controls (accent colours are
 * disabled for now, see the commented blocks). They drive the same
 * preferences (and the same saved setting) as the rest of the site, so the
 * choice persists across pages and visits.
 *
 * A plain group, not role="toolbar": a toolbar promises arrow-key navigation
 * between its buttons, which this does not implement.
 */
export function AccessibilityBar({ className = "" }: { className?: string }) {
  const { prefs, setPref } = useA11y();

  // The server and the first client render use default prefs, so resolve the
  // effective theme after mount (it depends on the OS setting).
  const [dark, setDark] = useState(false);
  useEffect(() => setDark(isDark(prefs)), [prefs]);

  const index = Math.max(0, FONT_SCALE_STEPS.findIndex((s) => s === prefs.fontScale));
  const atMin = index <= 0;
  const atMax = index >= FONT_SCALE_STEPS.length - 1;
  const step = (dir: -1 | 1) => {
    const next = FONT_SCALE_STEPS[Math.min(FONT_SCALE_STEPS.length - 1, Math.max(0, index + dir))];
    setPref("fontScale", next);
  };

  return (
    <div role="group" aria-label="Accessibility tools" className={`flex items-center gap-2 ${className}`}>
      <span className="mr-1 text-xs font-medium text-muted-foreground" aria-hidden="true">Text size</span>
      <button type="button" className={tool} aria-label="Decrease text size" aria-disabled={atMin} onClick={() => !atMin && step(-1)}>
        A<span aria-hidden="true">−</span>
      </button>
      <button type="button" className={tool} aria-label="Reset text size" onClick={() => setPref("fontScale", 1)}>
        A
      </button>
      <button type="button" className={tool} aria-label="Increase text size" aria-disabled={atMax} onClick={() => !atMax && step(1)}>
        A+
      </button>
      <span role="status" className="sr-only">{`Text size ${Math.round(prefs.fontScale * 100)}%`}</span>

      <span className="mx-1 hidden h-5 w-px bg-border md:block" aria-hidden="true" />

      <button
        type="button"
        className={tool}
        aria-label="High contrast"
        aria-pressed={prefs.contrast}
        title="High contrast"
        onClick={() => setPref("contrast", !prefs.contrast)}
      >
        <Contrast size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={tool}
        aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
        title={dark ? "Light theme" : "Dark theme"}
        onClick={() => setPref("theme", dark ? "light" : "dark")}
      >
        {dark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
      </button>

      {/* DISABLED: accent colour picker. Uncomment this to bring it back (see also the matching blocks in index.html, a11yPrefs.ts, AccessibilityBar.tsx, accessibilityBar.test.tsx).
      <span className="mx-1 hidden h-5 w-px bg-border md:block" aria-hidden="true" />

      <div role="group" aria-label="Accent colour" className="flex items-center gap-1.5">
        {PALETTES.map((pl) => (
          <button
            key={pl.id}
            type="button"
            className={swatch}
            aria-label={`${pl.label} accent colour`}
            aria-pressed={prefs.palette === pl.id}
            title={pl.label}
            onClick={() => setPref("palette", pl.id)}
          >
            <span
              className="h-4 w-4 rounded-full bg-[var(--sw)] dark:bg-[var(--swd)]"
              style={{ "--sw": pl.swatch, "--swd": pl.swatchDark } as React.CSSProperties}
              aria-hidden="true"
            />
          </button>
        ))}
      </div>
      */}
    </div>
  );
}
